import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from '@/components/charts/recharts';
import { TrendingUp, Wallet, PiggyBank, ArrowRight, Target, AlertTriangle, CheckCircle, Plus, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const defaultGoal = {
  id: Date.now(),
  goalName: '',
  targetAmount: 5000000,
  timeHorizon: 10,
  inflationLinked: true,
  inflationRate: 6,
  currentInvestment: 500000,
  monthlySIP: 15000,
  expectedReturn: 12,
  enableStepUp: false,
  stepUpType: 'percentage',
  stepUpValue: 10,
  // Input display states
  targetInput: '50,00,000',
  horizonInput: '10',
  inflationInput: '6',
  currentInput: '5,00,000',
  sipInput: '15,000',
  returnInput: '12',
};

export default function GoalPlannerCalculator() {
  const [goals, setGoals] = useState([{ ...defaultGoal }]);
  const [errors, setErrors] = useState({});

  // Indian number formatting
  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '';
    const numStr = Math.round(num).toString();
    let lastThree = numStr.substring(numStr.length - 3);
    let otherNumbers = numStr.substring(0, numStr.length - 3);
    if (otherNumbers !== '') lastThree = ',' + lastThree;
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${formatIndianNumber(value)}`;
  };

  const updateGoal = (index, field, value) => {
    setGoals(prev => {
      const newGoals = [...prev];
      newGoals[index] = { ...newGoals[index], [field]: value };
      return newGoals;
    });
  };

  const handleAmountChange = (index, field, inputField, e, min, max) => {
    const raw = e.target.value.replace(/,/g, '');
    updateGoal(index, inputField, e.target.value);
    const num = parseInt(raw, 10);
    const hasMin = min !== null && min !== undefined;
    if (!isNaN(num) && (!hasMin || num >= min) && num <= max) {
      updateGoal(index, field, num);
      setErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
    } else if (raw === '') {
      setErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
    } else {
      setErrors(prev => ({
        ...prev,
        [`${index}-${field}`]: hasMin
          ? `Enter value between ${formatIndianNumber(min)} and ${formatIndianNumber(max)}`
          : `Enter value up to ${formatIndianNumber(max)}`,
      }));
    }
  };

  const handleAmountBlur = (index, field, inputField, rawValue, min, max) => {
    const raw = String(rawValue ?? '').replace(/,/g, '').trim();
    const num = parseInt(raw, 10);
    const hasMin = min !== null && min !== undefined;
    const fallback = hasMin ? min : 0;
    const clamped = raw === ''
      ? (hasMin ? min : 0)
      : (hasMin
        ? Math.max(min, Math.min(max, Number.isFinite(num) ? num : fallback))
        : Math.max(0, Math.min(max, Number.isFinite(num) ? num : fallback)));
    updateGoal(index, field, clamped);
    updateGoal(index, inputField, formatIndianNumber(clamped));
    setErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
  };

  const handleRateChange = (index, field, inputField, e, min, max) => {
    updateGoal(index, inputField, e.target.value);
    const num = parseFloat(e.target.value);
    if (!isNaN(num) && num >= min && num <= max) {
      updateGoal(index, field, num);
      setErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
    } else if (e.target.value === '') {
      setErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
    } else {
      setErrors(prev => ({ ...prev, [`${index}-${field}`]: `Value must be between ${min}% and ${max}%` }));
    }
  };

  const handleRateBlur = (index, field, inputField, rawValue, min, max) => {
    const raw = String(rawValue ?? '').trim();
    const num = parseFloat(raw);
    const clamped = raw === '' || Number.isNaN(num)
      ? min
      : Math.max(min, Math.min(max, num));
    updateGoal(index, field, clamped);
    updateGoal(index, inputField, clamped.toString());
    setErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
  };

  const addGoal = () => {
    setGoals([...goals, { ...defaultGoal, id: Date.now(), goalName: `Goal ${goals.length + 1}` }]);
  };

  const removeGoal = (index) => {
    if (goals.length > 1) {
      setGoals(goals.filter((_, i) => i !== index));
    }
  };

  const calculateGoalResults = (goal) => {
    const { targetAmount, timeHorizon, inflationLinked, inflationRate, currentInvestment, monthlySIP, expectedReturn, enableStepUp, stepUpType, stepUpValue } = goal;
    
    const adjustedGoal = inflationLinked 
      ? targetAmount * Math.pow(1 + inflationRate / 100, timeHorizon)
      : targetAmount;
    
    const monthlyRate = expectedReturn / 100 / 12;
    const months = timeHorizon * 12;
    
    const fvCurrentInvestment = currentInvestment * Math.pow(1 + monthlyRate, months);
    
    const simulate = (startingSIP, collectYearlyData) => {
      let totalSIPInvestment = 0;
      let corpusValue = currentInvestment;
      let currentSIP = Math.max(0, startingSIP);
      const yearlyData = collectYearlyData ? [] : null;

      for (let month = 1; month <= months; month++) {
        totalSIPInvestment += currentSIP;
        corpusValue = (corpusValue + currentSIP) * (1 + monthlyRate);

        if (month % 12 === 0) {
          const year = month / 12;
          const goalAtYear = inflationLinked
            ? targetAmount * Math.pow(1 + inflationRate / 100, year)
            : targetAmount;

          if (yearlyData) {
            yearlyData.push({
              year: `Year ${year}`,
              yearNum: year,
              corpus: Math.round(corpusValue),
              goalValue: Math.round(goalAtYear),
              investment: Math.round(currentInvestment + totalSIPInvestment),
            });
          }

          if (enableStepUp) {
            currentSIP = stepUpType === 'percentage'
              ? currentSIP * (1 + stepUpValue / 100)
              : currentSIP + stepUpValue;
            currentSIP = Math.max(0, currentSIP);
          }
        }
      }

      return { projectedValue: corpusValue, totalSIPInvestment, yearlyData: yearlyData || [] };
    };

    const currentSim = simulate(monthlySIP, true);
    const projectedValue = currentSim.projectedValue;
    const difference = projectedValue - adjustedGoal;
    const isShortfall = difference < 0;
    
    const remainingRequired = adjustedGoal - fvCurrentInvestment;
    let requiredSIP = 0;
    if (remainingRequired > 0) {
      const target = adjustedGoal;
      const valueForSip = (sip) => simulate(sip, false).projectedValue;

      let low = 0;
      let high = Math.max(500, monthlySIP);
      let guard = 0;
      while (valueForSip(high) < target && high < 100000000 && guard < 50) {
        high *= 2;
        guard += 1;
      }

      if (valueForSip(high) >= target) {
        for (let i = 0; i < 34; i++) {
          const mid = (low + high) / 2;
          if (valueForSip(mid) >= target) high = mid;
          else low = mid;
        }
        requiredSIP = Math.ceil(high);
      } else {
        requiredSIP = Math.ceil(high);
      }
    }
    
    const additionalSIPNeeded = Math.max(0, requiredSIP - monthlySIP);

    return {
      originalGoal: targetAmount,
      adjustedGoal: Math.round(adjustedGoal),
      projectedValue: Math.round(projectedValue),
      difference: Math.round(Math.abs(difference)),
      isShortfall,
      requiredSIP: Math.round(requiredSIP),
      additionalSIPNeeded: Math.round(additionalSIPNeeded),
      totalInvestment: Math.round(currentInvestment + currentSim.totalSIPInvestment),
      totalReturns: Math.round(projectedValue - currentInvestment - currentSim.totalSIPInvestment),
      yearlyData: currentSim.yearlyData,
    };
  };

  const allResults = useMemo(() => {
    return goals.map(goal => ({
      goal,
      results: calculateGoalResults(goal),
    }));
  }, [goals]);

  // Combined summary
  const combinedSummary = useMemo(() => {
    return allResults.reduce((acc, { results }) => ({
      totalAdjustedGoal: acc.totalAdjustedGoal + results.adjustedGoal,
      totalProjectedValue: acc.totalProjectedValue + results.projectedValue,
      totalInvestment: acc.totalInvestment + results.totalInvestment,
      totalRequiredSIP: acc.totalRequiredSIP + results.requiredSIP,
      totalCurrentSIP: acc.totalCurrentSIP + goals.find(g => g.id === allResults.find(r => r.results === results).goal.id).monthlySIP,
    }), { totalAdjustedGoal: 0, totalProjectedValue: 0, totalInvestment: 0, totalRequiredSIP: 0, totalCurrentSIP: 0 });
  }, [allResults, goals]);

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            Goal Planner Calculator
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Plan for multiple financial goals - child's education, dream home, vacation, or anything else
          </p>
        </div>

        {/* Combined Summary (when multiple goals) */}
        {goals.length > 1 && (
          <div className="glass-effect rounded-2xl p-6 mb-8">
            <h2 className="font-heading text-xl font-bold text-text-primary mb-4">Combined Summary ({goals.length} Goals)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-text-muted text-xs">Total Goal Amount</p>
                <p className="text-xl font-bold text-primary">{formatCurrency(combinedSummary.totalAdjustedGoal)}</p>
              </div>
              <div className="text-center">
                <p className="text-text-muted text-xs">Total Projected Value</p>
                <p className="text-xl font-bold text-secondary">{formatCurrency(combinedSummary.totalProjectedValue)}</p>
              </div>
              <div className="text-center">
                <p className="text-text-muted text-xs">Current Monthly SIP</p>
                <p className="text-xl font-bold text-blue-400">{formatCurrency(combinedSummary.totalCurrentSIP)}</p>
              </div>
              <div className="text-center">
                <p className="text-text-muted text-xs">Required Monthly SIP</p>
                <p className="text-xl font-bold text-green-400">{formatCurrency(combinedSummary.totalRequiredSIP)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Goals */}
        {allResults.map(({ goal, results }, index) => (
          <div key={goal.id} className="mb-8 border border-white/10 rounded-2xl overflow-hidden">
            {/* Goal Header */}
            <div className="bg-white/5 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Target className="h-6 w-6 text-primary" />
                <input
                  type="text"
                  value={goal.goalName}
                  onChange={(e) => updateGoal(index, 'goalName', e.target.value)}
                  placeholder={`Goal ${index + 1}`}
                  className="bg-transparent text-xl font-bold text-text-primary focus:outline-none border-b border-transparent focus:border-primary"
                />
              </div>
              {goals.length > 1 && (
                <button
                  onClick={() => removeGoal(index)}
                  className="text-red-400 hover:text-red-300 transition-colors p-2"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Input Section */}
                <div className="space-y-5">
                  <div>
                    <label className="block text-text-secondary text-sm mb-2">Target Amount (₹)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={goal.targetInput}
                      onChange={(e) => handleAmountChange(index, 'targetAmount', 'targetInput', e, null, 1000000000)}
                      onBlur={(e) => handleAmountBlur(index, 'targetAmount', 'targetInput', e.target.value, null, 1000000000)}
                      onFocus={() => updateGoal(index, 'targetInput', goal.targetAmount.toString())}
                      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors[`${index}-targetAmount`] ? 'border-red-500' : 'border-white/10'}`}
                    />
                    {errors[`${index}-targetAmount`] && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {errors[`${index}-targetAmount`]}
                      </p>
                    )}
                    <input type="range" min="0" max="50000000" step="100000" value={goal.targetAmount}
                      onChange={(e) => { updateGoal(index, 'targetAmount', parseInt(e.target.value)); updateGoal(index, 'targetInput', formatIndianNumber(parseInt(e.target.value))); }}
                      className="w-full mt-2 accent-primary" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">Time Horizon (years)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={goal.horizonInput}
                        onChange={(e) => handleAmountChange(index, 'timeHorizon', 'horizonInput', e, 1, 40)}
                        onBlur={(e) => handleAmountBlur(index, 'timeHorizon', 'horizonInput', e.target.value, 1, 40)}
                        onFocus={() => updateGoal(index, 'horizonInput', goal.timeHorizon.toString())}
                        className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors[`${index}-timeHorizon`] ? 'border-red-500' : 'border-white/10'}`}
                      />
                    </div>
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">Expected Return (%) 2-12%</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={goal.returnInput}
                        onChange={(e) => handleRateChange(index, 'expectedReturn', 'returnInput', e, 2, 12)}
                        onBlur={(e) => handleRateBlur(index, 'expectedReturn', 'returnInput', e.target.value, 2, 12)}
                        onFocus={() => updateGoal(index, 'returnInput', goal.expectedReturn.toString())}
                        className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors[`${index}-expectedReturn`] ? 'border-red-500' : 'border-white/10'}`}
                      />
                      {errors[`${index}-expectedReturn`] && (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {errors[`${index}-expectedReturn`]}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Inflation Linked */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input type="checkbox" checked={goal.inflationLinked} onChange={(e) => updateGoal(index, 'inflationLinked', e.target.checked)}
                        className="w-5 h-5 rounded border-white/10 bg-white/5 text-primary focus:ring-primary" />
                      <span className="ml-3 text-text-primary font-medium">Inflation Linked</span>
                    </label>
                    {goal.inflationLinked && (
                      <div className="flex-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={goal.inflationInput}
                          onChange={(e) => handleRateChange(index, 'inflationRate', 'inflationInput', e, 2, 15)}
                          onBlur={(e) => handleRateBlur(index, 'inflationRate', 'inflationInput', e.target.value, 2, 15)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:border-primary"
                          placeholder="Inflation %"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">Current Investment (₹)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={goal.currentInput}
                        onChange={(e) => handleAmountChange(index, 'currentInvestment', 'currentInput', e, 0, 1000000000)}
                        onBlur={(e) => handleAmountBlur(index, 'currentInvestment', 'currentInput', e.target.value, 0, 1000000000)}
                        onFocus={() => updateGoal(index, 'currentInput', goal.currentInvestment.toString())}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">Monthly SIP (₹)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={goal.sipInput}
                        onChange={(e) => handleAmountChange(index, 'monthlySIP', 'sipInput', e, 500, 10000000)}
                        onBlur={(e) => handleAmountBlur(index, 'monthlySIP', 'sipInput', e.target.value, 500, 10000000)}
                        onFocus={() => updateGoal(index, 'sipInput', goal.monthlySIP.toString())}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <label className="flex items-center gap-3 text-text-primary select-none">
                      <input
                        type="checkbox"
                        checked={goal.enableStepUp}
                        onChange={(e) => updateGoal(index, 'enableStepUp', e.target.checked)}
                        className="h-5 w-5 accent-primary"
                      />
                      <span className="text-sm font-medium">Enable Step-Up SIP</span>
                    </label>

                    {goal.enableStepUp && (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center gap-6 text-sm text-text-secondary">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="radio"
                              name={`goal-stepup-type-${goal.id}`}
                              checked={goal.stepUpType === 'percentage'}
                              onChange={() => updateGoal(index, 'stepUpType', 'percentage')}
                              className="accent-primary"
                            />
                            <span>% annually</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="radio"
                              name={`goal-stepup-type-${goal.id}`}
                              checked={goal.stepUpType === 'fixed'}
                              onChange={() => updateGoal(index, 'stepUpType', 'fixed')}
                              className="accent-primary"
                            />
                            <span>Fixed amount</span>
                          </label>
                        </div>

                        <div>
                          <label className="block text-text-secondary text-sm mb-2">
                            {goal.stepUpType === 'percentage' ? 'Annual Step-Up (%)' : 'Annual Step-Up Amount (₹)'}
                          </label>
                          <input
                            type="text"
                            inputMode={goal.stepUpType === 'percentage' ? 'decimal' : 'numeric'}
                            value={goal.stepUpValue}
                            onChange={(e) => updateGoal(index, 'stepUpValue', Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                          />
                          <p className="text-text-muted text-xs mt-1">Applies once every 12 months.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Results Section */}
                <div className="space-y-4">
                  {/* Status Banner */}
                  <div className={`glass-effect rounded-xl p-4 border-l-4 ${results.isShortfall ? 'border-red-400' : 'border-green-400'}`}>
                    <div className="flex items-center gap-3">
                      {results.isShortfall ? (
                        <AlertTriangle className="h-6 w-6 text-red-400" />
                      ) : (
                        <CheckCircle className="h-6 w-6 text-green-400" />
                      )}
                      <div>
                        <p className={`font-semibold ${results.isShortfall ? 'text-red-400' : 'text-green-400'}`}>
                          {results.isShortfall ? 'Shortfall' : 'Surplus'}: {formatCurrency(results.difference)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass-effect rounded-xl p-4 text-center">
                      <p className="text-text-muted text-xs">{goal.inflationLinked ? 'Inflation-Adj Goal' : 'Target'}</p>
                      <p className="text-lg font-bold text-primary">{formatCurrency(results.adjustedGoal)}</p>
                    </div>
                    <div className="glass-effect rounded-xl p-4 text-center">
                      <p className="text-text-muted text-xs">Projected Value</p>
                      <p className="text-lg font-bold text-secondary">{formatCurrency(results.projectedValue)}</p>
                    </div>
                    <div className="glass-effect rounded-xl p-4 text-center">
                      <p className="text-text-muted text-xs">Total Investment</p>
                      <p className="text-lg font-bold text-blue-400">{formatCurrency(results.totalInvestment)}</p>
                    </div>
                    <div className="glass-effect rounded-xl p-4 text-center">
                      <p className="text-text-muted text-xs">Required SIP</p>
                      <p className="text-lg font-bold text-green-400">{formatCurrency(results.requiredSIP)}</p>
                    </div>
                  </div>

                  {/* Insight */}
                  {results.isShortfall && (
                    <div className="glass-effect rounded-xl p-3 border-l-4 border-primary">
                      <p className="text-text-secondary text-sm">
                        <span className="text-primary font-semibold">Insight:</span> Increase SIP by <span className="text-green-400 font-bold">{formatCurrency(results.additionalSIPNeeded)}/month</span>
                      </p>
                    </div>
                  )}

                  {/* Mini Chart */}
                  <div className="glass-effect rounded-xl p-4">
                    <h3 className="font-heading text-lg font-bold text-text-primary mb-3">Progress Over Time</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={results.yearlyData}
                          margin={{ top: 10, right: 16, left: 24, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} />
                          <YAxis
                            stroke="#94a3b8"
                            fontSize={12}
                            width={74}
                            tickMargin={8}
                            allowDecimals={false}
                            tickFormatter={(v) => formatCurrency(v)}
                          />
                          <Tooltip
                            formatter={(value, name) => [formatCurrency(value), name]}
                            labelFormatter={(label) => label}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="goalValue"
                            name="Target"
                            stroke="#ef4444"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="corpus"
                            name="Corpus"
                            stroke="#F5A623"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Add Goal Button */}
        <div className="text-center mb-8">
          <Button onClick={addGoal} variant="outline" className="border-primary text-primary hover:bg-primary/10">
            <Plus className="mr-2 h-5 w-5" /> Add Another Goal
          </Button>
        </div>

        {/* Disclaimer */}
        <div className="glass-effect rounded-xl p-4 border border-yellow-500/30">
          <p className="text-text-muted text-sm">
            <span className="text-yellow-500 font-semibold">Disclaimer:</span> The calculations shown are based on the inputs provided and assume constant returns throughout the investment period. Actual returns may vary based on market conditions, fund performance, and other factors. These projections are for illustrative purposes only and should not be considered as financial advice. Please consult a qualified financial advisor before making investment decisions.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Get a Personalized Investment Plan</h3>
            <p className="text-text-secondary mb-6">Let us help you create a customized investment strategy to achieve all your financial goals</p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
